import { useEffect, useState } from 'react';
import type { Member } from '../types';
import { fetchMembers, setMemberStatus } from '../lib/api';
import { LOCALES, LOCALE_META } from '../lib/locales';
import Avatar from '../components/Avatar';

/** Owner-only. New people land here as pending and see nothing until approved. */
export default function Members() {
  const [members, setMembers] = useState<Member[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => fetchMembers().then(setMembers).catch((e) => setError(e.message));
  useEffect(() => { void load(); }, []);

  async function act(m: Member, patch: Partial<Member>) {
    setBusy(m.id); setError(null);
    try { await setMemberStatus(m.id, patch); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  }

  const pending = members.filter((m) => m.status === 'pending');
  const rest = members.filter((m) => m.status !== 'pending');

  const card = (m: Member) => (
    <div className="sug" key={m.id}>
      <Avatar name={m.display_name} url={m.avatar_url} size={34} />
      <span className="body">
        <span className="rmeta">
          <span style={{ fontWeight: 600 }}>{m.display_name}</span>
          <span className="rpath">{m.email}</span>
          <span className="right">
            <span className={`pill ${m.status === 'active' ? 'p-ok' : m.status === 'pending' ? 'p-warn' : 'p-mute'}`}>
              {m.status}
            </span>
            {m.role === 'owner' && <span className="pill p-acc">owner</span>}
            {m.ai_editor && m.role !== 'owner' && <span className="pill p-acc">AI editor</span>}
          </span>
        </span>
        <span className="byline">
          {m.languages.length
            ? m.languages.map((l) => LOCALE_META[l]?.native ?? l).join(' · ')
            : 'no languages selected'}
        </span>
        <span className="acts">
          {m.status !== 'active' && (
            <button className="btn sm pri" disabled={busy === m.id}
              onClick={() => void act(m, { status: 'active' })}>Approve</button>
          )}
          {m.status === 'active' && m.role !== 'owner' && (
            <button className="btn sm" disabled={busy === m.id}
              onClick={() => void act(m, { status: 'blocked' })}>Remove access</button>
          )}
          {m.status === 'active' && m.role !== 'owner' && (
            <button className="btn sm" disabled={busy === m.id}
              title="AI editors can open the AI Lab and run model comparisons, which cost money"
              onClick={() => void act(m, { ai_editor: !m.ai_editor })}>
              {m.ai_editor ? 'Revoke AI Lab' : 'Allow AI Lab'}
            </button>
          )}
          {m.status === 'active' && (
            <select className="btn sm" value="" disabled={busy === m.id}
              onChange={(e) => {
                const l = e.target.value;
                if (l) void act(m, {
                  languages: m.languages.includes(l)
                    ? m.languages.filter((x) => x !== l) : [...m.languages, l],
                });
              }}>
              <option value="">Toggle a language…</option>
              {LOCALES.map((l) => (
                <option key={l} value={l}>
                  {m.languages.includes(l) ? '− remove ' : '+ add '}{LOCALE_META[l].native}
                </option>
              ))}
            </select>
          )}
        </span>
      </span>
    </div>
  );

  return (
    <>
      <div className="phead"><div><h1>People</h1>
        <p>Anyone who signs in lands here as pending and can see nothing at all until you approve
          them. Approving also decides which languages they may review. <b>AI editors</b> can
          additionally open the AI Lab; every run there is a paid generation, so hand it out
          deliberately.</p>
      </div></div>
      {error && <p className="hint" style={{ color: 'var(--crit)' }}>{error}</p>}

      {pending.length > 0 && (
        <div className="bsec">
          <div className="bsec-h"><span className="pill p-warn">{pending.length}</span>
            <span>Waiting for you</span></div>
          {pending.map(card)}
        </div>
      )}
      <div className="bsec">
        <div className="bsec-h"><span className="pill p-mute">{rest.length}</span><span>Everyone else</span></div>
        {rest.length ? rest.map(card)
          : <div className="panel"><div className="empty">Nobody else yet.</div></div>}
      </div>
    </>
  );
}
